import {BellOutlined, CloseOutlined, HomeOutlined} from '@ant-design/icons';
import {Drawer} from 'antd';
import React, {useEffect, useState} from 'react';
import {connect, useSelector} from 'react-redux';
import {createStructuredSelector} from 'reselect';

import {IconClose} from '../../components/Icons';
import drawerContentTypes from '../../consts/drawer/drawerContentTypes';
import {
    MAP_BUILDING_CLOSE,
    PROFILE_PAGE_NOTIFICATION_TAB,
    REG_FORM_BUILDING_INPUT_LABEL,
} from '../../consts/localization/localizationKeys';
import useIsMobileViewport from '../../hooks/useIsMobileViewport';
import useMenuScrollContainment from '../../hooks/useMenuScrollContainment';
import appRouterService from '../../services/app/appRouterService';
import {getLocalizedStrings} from '../../services/localization/localizationService';
import {hideDrawer} from '../../state/ducks/drawer';
import {makeNotificationsSelector} from '../../state/ducks/notifications';
import {makeSelectBuildingProfile} from '../../state/selectors/buildings';
import {makeSelectDrawerContentType, makeSelectIsDrawerVisible} from '../../state/selectors/drawer';
import {dispatch} from '../../state/store';
import MapBuildingDrawerContainer from './containers/MapBuildingDrawerContainer';
import NotificationsDrawerContainer from './containers/NotificationsDrawerContainer';

const COMPONENT_MAP = {
    [drawerContentTypes.MAP_OBJECT]: MapBuildingDrawerContainer,
    [drawerContentTypes.NOTIFICATIONS]: NotificationsDrawerContainer,
};

const ROOT_CLASS_NAME_MAP = {
    [drawerContentTypes.MAP_OBJECT]: 'map-building-drawer',
    [drawerContentTypes.NOTIFICATIONS]: 'notifications-drawer',
};

const mapStateToProps = createStructuredSelector({
    isOpen: makeSelectIsDrawerVisible(),
    contentType: makeSelectDrawerContentType(),
});

const AppDrawer = ({isOpen, contentType}) => {
    const isMobile = useIsMobileViewport();
    const [lastContentType, setLastContentType] = useState(contentType);
    const [isVisible, setIsVisible] = useState(false);
    const [panelNode, setPanelNode] = useState(null);
    const {unreadCount} = useSelector(makeNotificationsSelector());
    const buildingProfile = useSelector(makeSelectBuildingProfile());
    const renderedContentType = contentType || (isMobile ? lastContentType : null);
    const ContentComponent = COMPONENT_MAP[renderedContentType] || null;
    const isNotificationsDrawer = renderedContentType === drawerContentTypes.NOTIFICATIONS;
    const isMobileNotificationsDrawer = isMobile && isNotificationsDrawer;
    const isMobileBuildingDrawer = isMobile && renderedContentType === drawerContentTypes.MAP_OBJECT;
    const isMobilePanel = isMobileNotificationsDrawer || isMobileBuildingDrawer;
    const mobilePanelTitleId = isMobileNotificationsDrawer
        ? 'notifications-panel-title'
        : isMobileBuildingDrawer
        ? 'map-building-panel-title'
        : null;
    const isNotificationsDrawerOpen = isOpen && isNotificationsDrawer;
    const dispatchHideDrawer = () => dispatch(hideDrawer());
    const title = getLocalizedStrings()[PROFILE_PAGE_NOTIFICATION_TAB];
    const localizedStrings = getLocalizedStrings();
    const forwardToBuildingProfile = () => {
        if (!buildingProfile?.buildingID) return;
        // Release the map card before the profile page starts loading its own data.
        setLastContentType(null);
        dispatchHideDrawer();
        appRouterService.forwardToBuildingProfilePage(String(buildingProfile.buildingID));
    };

    useMenuScrollContainment(panelNode, isMobilePanel && (isOpen || isVisible));

    useEffect(() => {
        const dialog = panelNode?.closest('[role="dialog"]');

        if (mobilePanelTitleId) dialog?.setAttribute('aria-labelledby', mobilePanelTitleId);
        else dialog?.removeAttribute('aria-labelledby');
    }, [mobilePanelTitleId, panelNode]);

    useEffect(() => {
        if (contentType) setLastContentType(contentType);
    }, [contentType]);

    const handleAfterOpenChange = (opened) => {
        setIsVisible(opened);
        if (!opened) {
            panelNode?.closest('[role="dialog"]')?.removeAttribute('aria-labelledby');
            setLastContentType(null);
        }
        if (opened && isMobilePanel) {
            const dialog = panelNode?.closest('[role="dialog"]');

            dialog?.setAttribute('aria-labelledby', mobilePanelTitleId);
        }
    };

    useEffect(() => {
        if (!contentType && isOpen) {
            dispatchHideDrawer();
        }
    }, [contentType]);

    useEffect(() => {
        if (!isNotificationsDrawerOpen) return undefined;

        document.body.classList.add('notifications-drawer-open');

        return () => document.body.classList.remove('notifications-drawer-open');
    }, [isNotificationsDrawerOpen]);

    return (
        <Drawer
            onClose={dispatchHideDrawer}
            closeIcon={null}
            closable={isMobilePanel ? false : undefined}
            placement={isMobileBuildingDrawer ? 'bottom' : undefined}
            height={isMobileBuildingDrawer ? '74vh' : undefined}
            open={isOpen}
            autoFocus={isMobile ? false : undefined}
            afterOpenChange={handleAfterOpenChange}
            rootClassName={
                isMobileNotificationsDrawer
                    ? 'notifications-drawer mobile-notifications-panel'
                    : isMobileBuildingDrawer
                    ? 'map-building-drawer mobile-map-building-panel'
                    : ROOT_CLASS_NAME_MAP[renderedContentType]
            }
            zIndex={isMobilePanel ? 1100 : undefined}
            title={
                isMobileNotificationsDrawer ? (
                    <div className='notifications-panel-title'>
                        <span className='notifications-panel-icon' aria-hidden>
                            <BellOutlined />
                        </span>
                        <span id='notifications-panel-title'>{title}</span>
                        {unreadCount > 0 && (
                            <span className='notifications-panel-count'>{unreadCount > 99 ? '99+' : unreadCount}</span>
                        )}
                    </div>
                ) : isMobileBuildingDrawer ? (
                    <div className='map-building-panel-title'>
                        <HomeOutlined aria-hidden />
                        <span id='map-building-panel-title'>{localizedStrings[REG_FORM_BUILDING_INPUT_LABEL]}</span>
                    </div>
                ) : undefined
            }
            extra={
                isMobileNotificationsDrawer ? (
                    <button
                        type='button'
                        className='notifications-panel-close'
                        aria-label='Закрити сповіщення'
                        onClick={dispatchHideDrawer}
                    >
                        <CloseOutlined aria-hidden />
                    </button>
                ) : isMobileBuildingDrawer ? (
                    <div className='map-building-panel-tools'>
                        <button
                            type='button'
                            className='map-building-panel-close ant-drawer-close'
                            aria-label={localizedStrings[MAP_BUILDING_CLOSE]}
                            onClick={dispatchHideDrawer}
                        >
                            <CloseOutlined aria-hidden />
                        </button>
                    </div>
                ) : (
                    <IconClose className={'w-6 h-6 cursor-pointer p-1'} onClick={dispatchHideDrawer} />
                )
            }
            mask={isMobilePanel}
            width={isNotificationsDrawer ? 650 : 550}
            headerStyle={isMobilePanel ? undefined : {borderBottom: `none`, padding: `83px 10px 0px`}}
            bodyStyle={isMobilePanel ? undefined : {padding: `0 34px 0px`, overflowY: 'overlay'}}
        >
            {ContentComponent && (
                <ContentComponent
                    mobile={isMobilePanel}
                    panelRef={isNotificationsDrawer || isMobileBuildingDrawer ? setPanelNode : undefined}
                    onViewDetails={isMobileBuildingDrawer ? forwardToBuildingProfile : undefined}
                />
            )}
        </Drawer>
    );
};

export default connect(mapStateToProps)(AppDrawer);
